const time = (value) => {
  if (!value) return 0;
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  const result = date.getTime();
  return Number.isFinite(result) ? result : 0;
};

const zonedParts = (value, timezone) => Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
  timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
}).formatToParts(new Date(value)).map(({ type, value: part }) => [type, part]));

const dayAt = (value, timezone) => {
  const parts = zonedParts(value, timezone);
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const appointmentTime = (booking) => {
  const stored = time(booking.startAtDate || booking.startAt);
  if (stored) return stored;
  const day = String(booking.dateKey || '');
  const clock = String(booking.timeKey || '');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !/^\d{2}:\d{2}$/.test(clock)) return 0;
  const expected = new Date(`${day}T${clock}:00Z`).getTime();
  if (!Number.isFinite(expected)) return 0;
  let result = expected;
  // Legacy bookings can have only local date/time; never use the viewer's timezone.
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = zonedParts(result, booking.timezone || 'Europe/Rome');
    const observed = new Date(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}Z`).getTime();
    result += expected - observed;
  }
  const actual = zonedParts(result, booking.timezone || 'Europe/Rome');
  return `${actual.year}-${actual.month}-${actual.day}` === day && `${actual.hour}:${actual.minute}` === clock ? result : 0;
};

const result = (label, detail, tone) => ({ label, detail, tone });

/** Compare shared customer purchase evidence with this specific appointment. */
export function bookingPurchaseOutcome(booking = {}, info = {}, now = Date.now()) {
  if (!info.status || info.status === 'loading') {
    return result('Controllo in corso', 'Verifica degli acquisti del cliente.', 'pending');
  }
  if (info.status === 'error') {
    return result('Da verificare', 'Controllo acquisti non riuscito. Aggiorna la lista per riprovare.', 'warning');
  }
  if (info.status === 'unknown') {
    return result('Da verificare', 'Mancano email o profilo utente per controllare gli acquisti.', 'warning');
  }
  let startsAt;
  let bookingDay;
  let today;
  try {
    startsAt = appointmentTime(booking);
    if (!startsAt) throw new Error('Missing appointment date');
    bookingDay = dayAt(startsAt, booking.timezone || 'Europe/Rome');
    today = dayAt(now, booking.timezone || 'Europe/Rome');
  } catch (_) {
    return result('Da verificare', 'Data o orario dell’appuntamento non disponibili.', 'warning');
  }
  if (startsAt > now) {
    return result('Da svolgere', 'Il controllo degli acquisti successivi sarà disponibile dopo l’appuntamento.', 'pending');
  }
  const evidence = info.purchaseCheck;
  if (evidence?.version !== 1) {
    return result('Da verificare', 'I dati disponibili non consentono di confrontare acquisto e appuntamento.', 'warning');
  }
  const purchaseTime = Number(evidence.latestPurchaseTime) || 0;
  const purchaseDay = String(evidence.latestPurchaseDay || '');
  const exactPurchase = purchaseTime > startsAt && purchaseTime <= now;
  const laterDayPurchase = purchaseDay > bookingDay && purchaseDay <= today;
  if (exactPurchase || laterDayPurchase) {
    const date = exactPurchase ? new Date(purchaseTime) : new Date(`${purchaseDay}T12:00:00Z`);
    const label = date.toLocaleDateString('it-IT', {
      timeZone: booking.timezone || 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric',
    });
    return result('Acquistato dopo', `Acquisto registrato il ${label}, successivo all’appuntamento.`, 'success');
  }
  if (evidence.hasUnknownDates || Number(evidence.latestReviewTime) >= startsAt
    || String(evidence.latestReviewDay || '') >= bookingDay || purchaseDay === bookingDay
    || purchaseTime > now || purchaseDay > today) {
    return result('Da verificare', 'La data o lo stato di un acquisto non consentono un confronto certo con l’appuntamento.', 'warning');
  }
  if (purchaseTime || purchaseDay) {
    return result('Acquisto precedente', 'Risultano acquisti precedenti all’appuntamento; nessun nuovo acquisto successivo.', 'neutral');
  }
  return result('Nessun acquisto dopo', 'Non risultano acquisti pagati o rateizzati successivi all’appuntamento.', 'neutral');
}
